<script setup lang="ts">
import { computed, defineProps, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { ProjectVisibility } from '@/dashboard/types/project_visibility';

export type NotificationProps = {
  icon: string;
  title: string;
  ok_title: string;
  undo_title: string;
  link?: string;
  link_title?: string;
  ok?: () => void;
  undo?: () => void;
};

const props = defineProps<NotificationProps>();

const store = useStore();

const touch_start_y = ref(0);
const touch_end_y = ref(0);
const notification_ref = ref();

const styles = computed(() => {
  let diff = touch_end_y.value - touch_start_y.value;
  if (touch_end_y.value !== 0 && diff < 0) {
    return {
      'margin-top': `${touch_end_y.value !== 0 ? touch_end_y.value - touch_start_y.value : 0}px`,
    };
  } else {
    return {};
  }
});

function checkDirection() {
  let diff = touch_end_y.value - touch_start_y.value;
  if (diff < -50) {
    store.dispatch('notification_close');
  }
}

function touchStart(e: TouchEvent) {
  touch_start_y.value = e.changedTouches[0].screenY;
}

const toucheMove = (e: TouchEvent) => {
  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();
  touch_end_y.value = e.changedTouches[0].screenY;
};

function touchEnd(e: TouchEvent) {
  touch_end_y.value = e.changedTouches[0].screenY;
  checkDirection();
  touch_start_y.value = 0;
  touch_end_y.value = 0;
}

onMounted(() => {
  notification_ref.value?.addEventListener('touchstart', touchStart);
  notification_ref.value?.addEventListener('touchmove', toucheMove);
  notification_ref.value?.addEventListener('touchend', touchEnd);
});

onUnmounted(() => {
  notification_ref.value?.removeEventListener('touchstart', touchStart);
  notification_ref.value?.removeEventListener('touchmove', toucheMove);
  notification_ref.value?.removeEventListener('touchend', touchEnd);
});
</script>

<template>
  <div
    ref="notification_ref"
    :style="styles"
    class="tppr-banner pointer-events-auto h-xxl rounded-12 bg-gray-900 max-tablet:mx-s max-tablet:h-[56px] max-tablet:w-full max-tablet:justify-center"
  >
    <div class="tppr-banner__content">
      <span class="mr-[12px] text-[16px]">{{ icon }}</span>
      <span class="text-caption-s mr-s text-white">
        <span>{{ title }}</span>
        <a v-if="link" :href="link" target="_blank">
          <span>&nbsp;</span>
          <span class="underline underline-offset-[2px]">{{ link_title ?? 'More info' }} ↗</span>
        </a>
      </span>
      <span v-if="props.ok || props.undo" class="flex gap-xs">
        <button v-if="props.ok" class="tppr-button info-tip s1 s w-[112px] !rounded-4" @click="props.ok">
          <span class="tppr-button__text">{{ ok_title }}</span>
        </button>

        <button v-if="props.undo" class="tppr-button info-tip s2 s w-[112px] !rounded-4" @click="props.undo">
          <span class="tppr-button__text">{{ undo_title }}</span>
        </button>
      </span>
    </div>
  </div>
</template>
